/**
 * Composition Root for UI
 *
 * This is the single place where business logic (store) and UI components are wired together.
 * Views receive their dependencies through factory functions, enabling easy testing.
 *
 * Domain-specific wiring is delegated to modules in ./compositionRoot/.
 */

import type { RouteObject } from "react-router-dom";

import type { TAppStore } from "@/store";
import { createUseAppStore } from "@/store";

import { createApiClient, type TApiClient } from "@/apis/proda_finance";

import { wireWelcome } from "./compositionRoot/wireWelcome";
import { wireAdmin } from "./compositionRoot/wireAdmin";
import { wireAuditLog } from "./compositionRoot/wireAuditLog";
import { wireEmailCommunications } from "./compositionRoot/wireEmailCommunications";
import { wireSupport } from "./compositionRoot/wireSupport";
import { wireSettings } from "./compositionRoot/wireSettings";
import { wireRootLayout } from "./compositionRoot/wireRootLayout";
import { createRequireCustomerModule } from "./compositionRoot/RequireCustomerModule";
// The one place the shell registers customer modules.
import { REV_EU_SLUG, wireRevEU } from "@/modules/rev_eu";
import { LASALLE_SLUG, wireLaSalle } from "@/modules/lasalle";
import { LASALLE_BASE_PATH } from "@/modules/lasalle/router";
import { REV_APAC_SLUG, wireRevantageAPAC } from "@/modules/rev_apac";
import { REV_APAC_BASE_PATH } from "@/modules/rev_apac/router";

// Where each customer module serves its pages. Rev EU's predate customer
// modules and stay at the root; every other module lives under its own path.
const CUSTOMER_MODULE_BASE_PATHS: Record<string, string> = {
    [LASALLE_SLUG]: LASALLE_BASE_PATH,
    [REV_APAC_SLUG]: REV_APAC_BASE_PATH,
};
const customerModuleBasePath = (slug: string | null | undefined): string =>
    (slug && CUSTOMER_MODULE_BASE_PATHS[slug]) || "";

type TCompositionRootConfig = {
    store: TAppStore;
    apiBaseUrl?: string;
};

export const createCompositionRootUI = ({ store, apiBaseUrl = "/api" }: TCompositionRootConfig) => {
    const useAppStore = createUseAppStore(store);

    const api: TApiClient = createApiClient({
        baseUrl: apiBaseUrl,
        // Customer module routes go to the acting company's module (the viewed
        // company's for a Proda user viewing as another one).
        getCustomerModule: () => store.getState().user?.customerModule?.slug,
        requestInterceptors: [
            async (request) => {
                const state = store.getState();
                const companyId = state.effectiveCompanyId || state.user?.companyId;

                const { tokenService } = await import("@/auth/tokenService");
                const accessToken = await tokenService.getAccessToken();

                const headers: Record<string, string> = {
                    ...(request.headers as Record<string, string>),
                };

                if (companyId) {
                    headers["X-Company-ID"] = companyId;
                }

                const effectiveSubgroup = state.effectiveSubgroup;
                if (effectiveSubgroup) {
                    headers["X-Effective-Subgroup"] = effectiveSubgroup;
                }

                if (accessToken) {
                    headers["Authorization"] = `Bearer ${accessToken}`;
                }

                return {
                    ...request,
                    headers,
                };
            },
        ],
    });

    const deps = { store, useAppStore, api };

    const { ViewWelcome } = wireWelcome(deps);
    const { ViewCompanies, ViewUsers, ViewAnnouncements } = wireAdmin(deps);
    const { ViewAuditLog, ViewGlobalAuditLog } = wireAuditLog(deps);
    const { ViewEmailCommunications } = wireEmailCommunications(deps);
    const { ViewSupportTickets, ViewSupportTicketDetail, ViewForwardedTickets } = wireSupport(deps);
    const { ViewSettings } = wireSettings(deps);
    const { routes: revEuRoutes, ...revEuViews } = wireRevEU(deps);
    const { routes: laSalleRoutes } = wireLaSalle(deps);
    const { routes: revAPACRoutes } = wireRevantageAPAC(deps);
    const RequireCustomerModule = createRequireCustomerModule(useAppStore);
    const {
        RootLayout,
        LandingRedirect,
        adminOnlyRoutes: _adminOnlyRoutes,
        prodaOnlyRoutes: _prodaOnlyRoutes,
    } = wireRootLayout(deps, ViewSettings, customerModuleBasePath);

    const routes: RouteObject[] = [
        {
            path: "/",
            element: <RootLayout />,
            children: [
                // Where "/" lands depends on the caller's permissions and the
                // Homepage v2 flag, so it is resolved at render time rather
                // than hardcoded here.
                { index: true, element: <LandingRedirect /> },
                { path: "welcome", element: <ViewWelcome /> },
                { path: "support", element: <ViewSupportTickets /> },
                { path: "support/:ticketId", element: <ViewSupportTicketDetail /> },
                { path: "admin/audit-log", element: <ViewAuditLog /> },
                { path: "admin/email-communications", element: <ViewEmailCommunications /> },
                { path: "admin/global-audit-log", element: <ViewGlobalAuditLog /> },
                { path: "admin/companies", element: <ViewCompanies /> },
                { path: "admin/support-forwarded", element: <ViewForwardedTickets /> },
                { path: "admin/users", element: <ViewUsers /> },
                { path: "admin/announcements", element: <ViewAnnouncements /> },
                // Customer module routes (Rev EU). The API gates them per company;
                // the guard sends a company on another module to its landing page.
                {
                    element: <RequireCustomerModule slug={REV_EU_SLUG} />,
                    children: revEuRoutes,
                },
                {
                    path: LASALLE_BASE_PATH.slice(1),
                    element: <RequireCustomerModule slug={LASALLE_SLUG} />,
                    children: laSalleRoutes,
                },
                {
                    path: REV_APAC_BASE_PATH.slice(1),
                    element: <RequireCustomerModule slug={REV_APAC_SLUG} />,
                    children: revAPACRoutes,
                },
            ],
        },
    ];

    return {
        routes,
        ...revEuViews,
        ViewSettings,
        ViewAuditLog,
        ViewEmailCommunications,
        ViewGlobalAuditLog,
        ViewCompanies,
        ViewUsers,
        ViewAnnouncements,
        ViewSupportTickets,
        ViewSupportTicketDetail,
        ViewForwardedTickets,
    };
};
